// Slice 40 — shell-level context exposing { username, openIssueKey, signOut }
// to every screen. Slice 41 will fill `openIssueKey` with the real Issue
// License modal; for now it surfaces a "Coming in Slice 41" toast so the
// ⌘N hotkey + sidebar CTA still feel alive.

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Api, type StaffRole } from "../api/client";

export interface HqShellState {
  username: string;
  /** Slice 48 — role surfaces from /admin/session; gates Issue/edit buttons. */
  role: StaffRole;
  openIssueKey: () => void;
  /**
   * Slice 41 wires this to the Licenses screen's modal. When set, ⌘N + the
   * sidebar CTA flip the boolean instead of toasting. The Licenses screen
   * also calls `bumpRefresh()` after a successful issue so its table
   * re-fetches without a manual reload.
   */
  registerIssueHandler: (handler: (() => void) | null) => void;
  refreshTick: number;
  bumpRefresh: () => void;
  signOut: () => Promise<void>;
  toast: ToastFn;
}

export type ToastTone = "success" | "warn" | "danger" | "info";
export type ToastFn = (message: string, tone?: ToastTone) => void;

interface Toast {
  id: number;
  message: string;
  tone: ToastTone;
}

const Ctx = createContext<HqShellState | null>(null);

export interface HqShellProviderProps {
  username: string;
  role: StaffRole;
  onSignedOut: () => void;
  children: ReactNode;
}

export function HqShellProvider({ username, role, onSignedOut, children }: HqShellProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [refreshTick, setRefreshTick] = useState(0);
  const issueHandlerRef = useRef<(() => void) | null>(null);

  const toast = useCallback<ToastFn>((message, tone = "info") => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, tone }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
  }, []);

  const openIssueKey = useCallback(() => {
    const h = issueHandlerRef.current;
    if (h) {
      h();
    } else {
      // No screen registered a handler yet — Slice 41 Licenses screen does;
      // any other route falls back to the toast.
      toast("Open the Licenses screen to issue a key.", "info");
    }
  }, [toast]);

  const registerIssueHandler = useCallback((handler: (() => void) | null) => {
    issueHandlerRef.current = handler;
  }, []);

  const bumpRefresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  const signOut = useCallback(async () => {
    await Api.logout();
    onSignedOut();
  }, [onSignedOut]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && (e.key === "n" || e.key === "N")) {
        e.preventDefault();
        openIssueKey();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openIssueKey]);

  const value = useMemo<HqShellState>(
    () => ({
      username,
      role,
      openIssueKey,
      registerIssueHandler,
      refreshTick,
      bumpRefresh,
      signOut,
      toast,
    }),
    [username, role, openIssueKey, registerIssueHandler, refreshTick, bumpRefresh, signOut, toast],
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      <ToastStack toasts={toasts} />
    </Ctx.Provider>
  );
}

export function useShell(): HqShellState {
  const c = useContext(Ctx);
  if (!c) throw new Error("useShell outside <HqShellProvider>");
  return c;
}

function ToastStack({ toasts }: { toasts: Toast[] }) {
  if (toasts.length === 0) return null;
  return (
    <div
      style={{
        position: "fixed",
        right: 20,
        bottom: 20,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        zIndex: 300,
      }}
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          className="hq-fade-in"
          style={{
            padding: "10px 14px",
            borderRadius: "var(--hq-radius-lg)",
            background: "var(--hq-bg-raised)",
            border: `1px solid ${toneBorder(t.tone)}`,
            color: toneFg(t.tone),
            boxShadow: "0 10px 30px rgba(0,0,0,.4)",
            fontSize: 12.5,
            maxWidth: 360,
          }}
        >
          {t.message}
        </div>
      ))}
    </div>
  );
}

function toneBorder(tone: ToastTone): string {
  switch (tone) {
    case "success":
      return "rgba(52,211,153,.35)";
    case "warn":
      return "rgba(251,191,36,.35)";
    case "danger":
      return "rgba(251,113,133,.35)";
    case "info":
      return "rgba(34,211,238,.35)";
  }
}

function toneFg(tone: ToastTone): string {
  switch (tone) {
    case "success":
      return "var(--hq-success)";
    case "warn":
      return "var(--hq-warn)";
    case "danger":
      return "var(--hq-danger)";
    case "info":
      return "var(--hq-accent)";
  }
}
